import {
  Button,
  FormControl,
  Grid,
  TextField
} from "@mui/material";
import { useState } from "react";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Select from "../../../components/common/Select";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";
import { formatDateToYMD, formatISOToDate } from "../../../utils/helpers";

const ProfessionalRoleForm = ({
  role = {},
  onSubmit,
  onCancel,
  errors,
  setErrors,
  dropDownOptions
}) => {
  const [formData, setFormData] = useState({
    id: role?.id || null,
    aviation_category: role?.aviation_category || "",
    other_aviation_category: role?.other_aviation_category || "",
    title: role?.title || "",
    aircraft_type_experience: role?.aircraft_type_experience || "",
    icao_type_ratings: role?.icao_type_ratings || "",
    icao_ratings_expiry: role?.icao_ratings_expiry || null,
    regulatory_body: role?.regulatory_body || "",
    other_regulatory_body: role?.other_regulatory_body || "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const updatedErrors = { ...errors };
      delete updatedErrors[field];
      setErrors(updatedErrors);
    }
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {};

    if (!formData.title?.trim()) {
      newErrors.title = "Title is required";
      valid = false;
    }
    if (!formData.aviation_category) {
      newErrors.aviation_category = "Aviation category is required";
      valid = false;
    } else if (formData.aviation_category === "other" && !formData.other_aviation_category?.trim()) {
      newErrors.other_aviation_category = "Please specify category";
      valid = false;
    }
    if (!formData.regulatory_body?.trim()) {
      newErrors.regulatory_body = "Regulatory body is required";
      valid = false;
    } else if (formData.regulatory_body === "other" && !formData.other_regulatory_body?.trim()) {
      newErrors.other_regulatory_body = "Please specify regulatory body";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 my-3 sentry-mask">
      {/* Aviation Category */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              error={!!errors.aviation_category}
              helperText={errors.aviation_category}
              label={
                <span>
                  Aviation Category <RequiredAsterisk />
                </span>
              }
              onChange={(value) => handleChange("aviation_category", value)}
              options={dropDownOptions.aviationCategoryChoices}
              value={formData.aviation_category}
            />
          </Grid>

          {formData.aviation_category === "other" && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="other_aviation_category"
                label={
                  <span>
                    Specify Category <RequiredAsterisk />
                  </span>
                }
                value={formData.other_aviation_category}
                onChange={(e) =>
                  handleChange("other_aviation_category", e.target.value)
                }
                error={!!errors.other_aviation_category}
                helperText={errors.other_aviation_category}
                fullWidth
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              error={!!errors.regulatory_body}
              helperText={errors.regulatory_body}
              label={
                <span>
                  Regulatory Body <RequiredAsterisk />
                </span>
              }
              onChange={(value) => handleChange("regulatory_body", value)}
              options={dropDownOptions.icaoIssuingAuthorityChoices}
              value={formData.regulatory_body}
            />
          </Grid>

          {formData.regulatory_body === "other" && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="other_regulatory_body"
                label={
                  <span>
                    Specify Regulatory Body <RequiredAsterisk />
                  </span>
                }
                value={formData.other_regulatory_body}
                onChange={(e) =>
                  handleChange("other_regulatory_body", e.target.value)
                }
                error={!!errors.other_regulatory_body}
                helperText={errors.other_regulatory_body}
                fullWidth
              />
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <TextField
              id="title"
              placeholder="E.g., Captain, First Officer, A&P Technician, etc."
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              label={
                <span>
                  Rank/Title <RequiredAsterisk />
                </span>
              }
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              id="icao_type_ratings"
              placeholder="78"
              value={formData.icao_type_ratings}
              onChange={(e) =>
                handleChange("icao_type_ratings", e.target.value)
              }
              label="ICAO Type Ratings"
              error={!!errors.icao_type_ratings}
              helperText={errors.icao_type_ratings}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth error={!!errors[`icao_ratings_expiry`]}>
              <DatePicker
                label="ICAO Ratings Expiry Date"
                value={formatISOToDate(formData.icao_ratings_expiry)}
                onChange={(date) =>
                  handleChange("icao_ratings_expiry", formatDateToYMD(date))
                }
                slotProps={{
                  popper: {
                    placement: "auto",
                    modifiers: [
                      {
                        name: "flip",
                        enabled: true,
                      },
                      {
                        name: "preventOverflow",
                        enabled: true,
                        options: {
                          boundary: "viewport",
                        },
                      },
                    ],
                  },
                  textField: {
                    fullWidth: true,
                    error: !!errors[`icao_ratings_expiry`],
                    helperText: errors[`icao_ratings_expiry`],
                  },
                }}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              id="aircraft_type_experience"
              placeholder="e.g., Boeing 737, Airbus A320"
              value={formData.aircraft_type_experience}
              onChange={(e) =>
                handleChange("aircraft_type_experience", e.target.value)
              }
              label="Aircraft Type Experience"
              error={!!errors.aircraft_type_experience}
              helperText={errors.aircraft_type_experience}
              multiline
              minRows={3}
              fullWidth
            />
          </Grid>
        </Grid>
        <div className="flex justify-end space-x-2 mt-4">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </div>
      </LocalizationProvider>
    </form>
  );
};

export default ProfessionalRoleForm;
